import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ChevronRight,
  Check,
  Heart,
  Minus,
  Plus,
  RotateCcw,
  Ruler,
  Share2,
  ShoppingBag,
  Truck,
} from 'lucide-react';
import { isApiEnabled } from '../config/env';
import { modamaiTrace } from '../utils/modamaiTrace';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchProductDetail } from '../store/actions/productActions';
import { ProductCard } from '../components/commerce/ProductCard';
import { ProductCardsGrid } from '../components/commerce/ProductCardLayout';
import ProductImageGallery from '../components/commerce/ProductImageGallery';
import ProductDetailTabs from '../components/commerce/ProductDetailTabs';
import ProductReviewsSection from '../components/reviews/ProductReviewsSection';
import StarRating from '../components/reviews/StarRating';
import { useCart } from '../hooks/useCart';
import { useWishlist } from '../hooks/useWishlist';
import { formatPrice } from '../utils/formatPrice';
import { Container } from '../components/layout/Primitives';
import ApiLocaleSeo from '../components/seo/ApiLocaleSeo';
import PageSeo from '../components/seo/PageSeo';
import { isSizeAvailable } from '../utils/catalogPdp';
import { productService } from '../services/productService';
import { SUPPORTED_LOCALES } from '../config/locales';

const BreadcrumbNav = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-family: ${({ theme }) => theme.fonts.sans};
`;

const CrumbCurrent = styled.span`
  color: ${({ theme }) => theme.colors.foreground};
`;

const CrumbLink = styled(Link)`
  transition: color 0.2s ease;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  padding-bottom: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
    gap: clamp(40px, 4vw, 64px);
  }
`;

const ImageCol = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const ShareBtn = styled.button`
  padding: 10px;
  border: none;
  border-radius: 999px;
  background: hsla(38, 30%, 98%, 0.9);
  backdrop-filter: blur(6px);
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground};
  transition: background 0.2s ease;
  &:hover {
    background: hsla(40, 22%, 96%, 0.98);
  }
`;

const DetailCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: min(40rem, 100%);
  padding: clamp(12px, 2vw, 20px) 0;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const BadgeSale = styled.span`
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.champagneDark};
  color: ${({ theme }) => theme.colors.cream};
`;

const BadgeNew = styled.span`
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.cream};
`;

const BrandLine = styled.p`
  margin: 0 0 6px;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Title = styled.h1`
  margin: 0 0 14px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.85rem, 2.2vw + 1rem, 2.85rem);
  font-weight: 300;
  line-height: 1.18;
`;

const RatingHeroBtn = styled.button`
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.2vw, 14px);
  margin: -4px 0 14px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.82;
  }
`;

const RatingHeroText = styled.span`
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: underline;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 6px;
`;

const Price = styled.span`
  font-size: clamp(1.35rem, 1.2vw + 1rem, 1.75rem);
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 300;
`;

const WasPrice = styled.span`
  font-size: ${({ theme }) => theme.type.body};
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: line-through;
`;

const SaleEnd = styled.p`
  margin: -2px 0 16px;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.terracottaDark};
`;

const TaxNote = styled.p`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ColorRow = styled.div`
  margin-bottom: 18px;
`;

const ColorHead = styled.div`
  margin-bottom: 10px;
`;

const ColorLabel = styled.span`
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const ColorSwatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const ColorSwatchBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.foreground : theme.colors.border)};
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $hex }) => $hex || '#ccc'};
  transition: border-color 0.2s ease;
  box-shadow: inset 0 0 0 1px hsla(0, 0%, 100%, 0.28);

  &:hover {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const SizeRow = styled.div`
  margin-bottom: 20px;
`;

const SizeHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const SizeGuideLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: underline;
`;

const SizeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SizeBtn = styled.button`
  min-width: 46px;
  height: 44px;
  padding: 0 14px;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.05em;
  border: 1px solid
    ${({ $active, $disabled, theme }) =>
      $disabled ? theme.colors.border : $active ? theme.colors.foreground : theme.colors.border};
  background: ${({ $active, $disabled, theme }) =>
    $disabled ? theme.colors.secondary : $active ? theme.colors.foreground : 'transparent'};
  color: ${({ $active, $disabled, theme }) =>
    $disabled
      ? theme.colors.mutedForeground
      : $active
        ? theme.colors.cream
        : theme.colors.foreground};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  text-decoration: ${({ $disabled }) => ($disabled ? 'line-through' : 'none')};
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, opacity 0.2s ease;
  font-family: ${({ theme }) => theme.fonts.sans};
  &:hover {
    border-color: ${({ $disabled, theme }) => (!$disabled ? theme.colors.foreground : theme.colors.border)};
  }
`;

const QtyBlock = styled.div`
  margin-bottom: 20px;
`;

const QtyLabel = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  width: fit-content;
`;

const QtyAdj = styled.button`
  padding: 12px 14px;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  transition: background 0.2s ease;
  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

const QtyVal = styled.span`
  padding: 0 20px;
  font-size: ${({ theme }) => theme.type.body};
  min-width: 40px;
  text-align: center;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: clamp(28px, 4vw, 36px);
`;

const AddBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.sans};
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.cream};
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const WishLarge = styled.button`
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  cursor: pointer;
  transition: border-color 0.2s ease;
  color: inherit;
  &:hover {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Highlights = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  padding: 24px 0 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ShippingPanel = styled.div`
  margin: 0 0 20px;
  border: 1px solid ${({ theme }) => theme.colors.champagneDark};
  background: linear-gradient(135deg, hsla(38, 54%, 95%, 0.82), ${({ theme }) => theme.colors.surface});
  padding: 14px 14px 12px;
  border-radius: 10px;
`;

const ShippingTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ShippingTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ShippingSelect = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.champagneDark};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  padding: 8px 10px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  border-radius: 8px;
`;

const ShippingPill = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.terracottaDark};
  background: hsla(13, 47%, 93%, 0.55);
  padding: 10px;
  margin-bottom: 10px;
  font-size: ${({ theme }) => theme.type.bodySm};
  border-radius: 8px;
  line-height: 1.4;
`;

const ShippingList = styled.div`
  display: grid;
  gap: 8px;
`;

const ShippingRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: ${({ theme }) => theme.type.bodySm};
  padding: 0.55rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  align-items: center;
`;

const ShippingMethod = styled.span`
  font-weight: 500;
`;

const EtaText = styled.span`
  color: ${({ theme }) => theme.colors.mutedForeground};
  white-space: nowrap;
`;

const FastestRow = styled(ShippingRow)`
  border-color: ${({ theme }) => theme.colors.terracottaDark};
  background: hsla(13, 52%, 91%, 0.5);
`;

const FastestBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.cream};
  background: ${({ theme }) => theme.colors.terracottaDark};
`;

const HiItem = styled.div`
  text-align: center;
`;

const HiText = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const RelatedSection = styled.section`
  margin-top: clamp(56px, 8vw, 88px);
  padding-bottom: clamp(56px, 10vw, 96px);
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: clamp(44px, 6vw, 72px);
`;

const RelatedTitle = styled.h2`
  margin: 0 0 28px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.sectionTitle};
  font-weight: 300;
`;

const RelatedGrid = styled(ProductCardsGrid)``;

const NotFound = styled.div`
  text-align: center;
  padding: clamp(72px, 14vw, 120px) 16px;
`;

const NotFoundTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 3vw + 1rem, 3rem);
  font-weight: 300;
`;

const BackShop = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    background: ${({ theme }) => theme.colors.foreground};
    color: ${({ theme }) => theme.colors.cream};
  }
`;

function lightSwatchFg(hex) {
  const light = ['#FFFDD0', '#FFFFF0', '#FFD700', '#FFFFFF', '#FFFF00', '#FFC0CB', '#F4ECD9'];
  return light.includes(String(hex).toUpperCase()) ? 'hsl(0, 0%, 12%)' : 'hsl(0, 0%, 98%)';
}

function formatEtaDays(opt) {
  if (!opt) return 'ETA unavailable';
  const min = Number.isFinite(opt.etaMinDays) ? opt.etaMinDays : null;
  const max = Number.isFinite(opt.etaMaxDays) ? opt.etaMaxDays : null;
  if (min != null && max != null && min !== max) return `${min}-${max} days`;
  if (min != null) return `${min} days`;
  if (max != null) return `${max} days`;
  const raw = String(opt.etaLabel || '').trim();
  if (!raw) return 'ETA unavailable';
  return /\bdays?\b/i.test(raw) ? raw : `${raw} days`;
}

function sortByQuickest(options) {
  const rows = Array.isArray(options) ? [...options] : [];
  return rows.sort((a, b) => {
    const aEta = Number.isFinite(a?.etaMinDays) ? Number(a.etaMinDays) : Number.MAX_SAFE_INTEGER;
    const bEta = Number.isFinite(b?.etaMinDays) ? Number(b.etaMinDays) : Number.MAX_SAFE_INTEGER;
    if (aEta !== bEta) return aEta - bEta;
    const aMax = Number.isFinite(a?.etaMaxDays) ? Number(a.etaMaxDays) : Number.MAX_SAFE_INTEGER;
    const bMax = Number.isFinite(b?.etaMaxDays) ? Number(b.etaMaxDays) : Number.MAX_SAFE_INTEGER;
    if (aMax !== bMax) return aMax - bMax;
    return String(a?.methodName || '').localeCompare(String(b?.methodName || ''));
  });
}

function toVariantId(variantLike) {
  if (!variantLike) return '';
  if (typeof variantLike === 'string') return String(variantLike).trim();
  return String(variantLike.variantId || variantLike.vid || variantLike.id || '').trim();
}

function resolveSelectedVariantId(item, selectedColor, selectedSize) {
  if (!item) return '';

  const byCell =
    selectedColor && selectedSize ? item?.colorSizeMap?.[selectedColor]?.[selectedSize] : null;
  if (byCell) {
    const fromCell = toVariantId(byCell);
    if (fromCell) return fromCell;
  }

  const variants = Array.isArray(item.variants) ? item.variants : [];
  if (selectedColor && selectedSize) {
    const c = String(selectedColor).toLowerCase();
    const s = String(selectedSize).toLowerCase();
    const fuzzy = variants.find((v) => {
      const vk = String(v?.variantKey || '').toLowerCase();
      const vn = String(v?.variantNameEn || v?.name || '').toLowerCase();
      return (vk.includes(c) && vk.includes(s)) || (vn.includes(c) && vn.includes(s));
    });
    if (fuzzy) {
      const fromFuzzy = toVariantId(fuzzy);
      if (fromFuzzy) return fromFuzzy;
    }
  }

  const defaultVariantId = String(item.defaultVariantId || item.variantId || '').trim();
  if (defaultVariantId) return defaultVariantId;

  const firstWithId = variants.find((v) => toVariantId(v));
  return firstWithId ? toVariantId(firstWithId) : '';
}

export default function Product() {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const dispatch = useAppDispatch();
  const apiRaw = useAppSelector((s) => s.product.detail);
  const detailMatchesParam = useMemo(() => {
    if (!apiRaw || !slug) return false;
    const token = String(slug).trim();
    if (!token) return false;
    const bySlug = String(apiRaw.slug || '').trim() === token;
    const byMongoId = String(apiRaw._id || '').trim() === token;
    const byMappedId = String(apiRaw.id || '').trim() === token;
    return bySlug || byMongoId || byMappedId;
  }, [apiRaw, slug]);
  const apiDetail = detailMatchesParam ? apiRaw : null;
  const detailLoading = useAppSelector((s) => s.product.detailLoading);
  const detailError = useAppSelector((s) => s.product.detailError);

  const shouldTryApi = isApiEnabled() && Boolean(slug);
  const showApiLoading = shouldTryApi && (detailLoading || (!detailError && !apiDetail));

  const base = useMemo(() => {
    if (!isApiEnabled()) return null;
    return detailMatchesParam ? apiDetail : null;
  }, [apiDetail, detailMatchesParam]);

  const item = useMemo(() => {
    if (!isApiEnabled()) return null;
    if (detailMatchesParam) return apiDetail;
    return null;
  }, [apiDetail, detailMatchesParam]);

  useEffect(() => {
    if (!shouldTryApi) {
      modamaiTrace('Product.jsx skip fetchProductDetail', {
        slug,
        shouldTryApi,
        apiOn: isApiEnabled(),
      });
      return undefined;
    }
    modamaiTrace('Product.jsx dispatch fetchProductDetail', { slug });
    dispatch(fetchProductDetail(slug));
    return undefined;
  }, [dispatch, slug, shouldTryApi]);

  const { add: addToCart } = useCart();
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();

  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [qty, setQty] = useState(1);
  const reviewsRef = useRef(null);
  const [reviewMeta, setReviewMeta] = useState(null);
  const localeShippingCountry = useMemo(() => {
    const byCode = SUPPORTED_LOCALES.find((l) => l.code === String(i18n?.language || '').trim());
    return byCode?.shippingCountryCode || 'US';
  }, [i18n?.language]);
  const [shippingCountry, setShippingCountry] = useState('US');
  const [shippingState, setShippingState] = useState({ loading: false, options: [], fastestOption: null });
  const shippingCountries = useMemo(() => {
    const map = new Map();
    for (const l of SUPPORTED_LOCALES) {
      if (l.shippingCountryCode && l.country && !map.has(l.shippingCountryCode)) {
        map.set(l.shippingCountryCode, l.country);
      }
    }
    return Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  }, []);

  const onReviewsMetaLoaded = useCallback((meta) => {
    setReviewMeta(meta);
  }, []);

  useEffect(() => {
    if (!item) return undefined;
    const firstColor = item.colors?.[0]?.name ?? '';
    let firstSize = '';
    if (firstColor) {
      const allowed = item.sizes?.filter((s) => isSizeAvailable(item, firstColor, s));
      firstSize = allowed?.[0] ?? '';
    }
    if (!firstSize && item.sizes?.length === 1) firstSize = item.sizes[0];
    setColor(firstColor);
    setSize(firstSize);
    setQty(1);
    document.title = t('modamai.product.pageDocumentTitle', { name: item.name });
    return () => {
      document.title = t('modamai.product.siteTitle');
    };
  }, [slug, item, t]);

  useEffect(() => {
    setReviewMeta(null);
  }, [slug]);

  const apiCatalog = useAppSelector((s) =>
    Array.isArray(s.product?.shopCatalogList) ? s.product.shopCatalogList : [],
  );

  const related = useMemo(
    () => {
      if (!base || !isApiEnabled()) return [];
      if (!apiCatalog.length) return [];
      return apiCatalog
        .filter((p) => p.id !== base.id && p.category === base.category)
        .slice(0, 4);
    },
    [base, apiCatalog],
  );

  useEffect(() => {
    modamaiTrace('Product.jsx detail snapshot', {
      slug,
      detailLoading,
      detailError,
      apiRawSlug: apiRaw?.slug,
      resolvedMatch: Boolean(detailMatchesParam),
      apiDetailPresent: Boolean(apiDetail),
      shopCatalogListLenForRelated: apiCatalog.length,
      relatedMemoLen: related.length,
    });
  }, [
    slug,
    detailLoading,
    detailError,
    apiRaw,
    detailMatchesParam,
    apiDetail,
    apiCatalog.length,
    related.length,
  ]);

  const hasCompare =
    item &&
    item.comparePrice != null &&
    Number(item.comparePrice) > Number(item.price);
  const taggedSale = item?.tags.includes('sale');
  const showSaleBadge = Boolean(taggedSale && hasCompare);
  const salePercent =
    showSaleBadge &&
    Math.max(3, Math.round((1 - item.price / item.comparePrice) * 100));

  const inWishlist = base ? isWishlisted(base.id) : false;

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shareProduct = async () => {
    if (!item) return;
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: item.name, url });
      else await navigator.clipboard.writeText(url);
    } catch {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* noop */
      }
    }
  };

  const handleColorSelect = (colorName) => {
    setColor(colorName);
    setSize((prev) => {
      if (prev && isSizeAvailable(item, colorName, prev)) return prev;
      const next = item?.sizes?.find((s) => isSizeAvailable(item, colorName, s));
      return next || '';
    });
  };

  const handleAdd = () => {
    if (!base || !item) return;
    if (item.sizes?.length && !size) return;
    if (item.colors?.length && !color) return;
    addToCart({
      product: base,
      size,
      color,
      qty,
    });
  };

  useEffect(() => {
    setShippingCountry(localeShippingCountry);
  }, [localeShippingCountry]);

  const selectedVariantId = useMemo(
    () => resolveSelectedVariantId(item, color, size),
    [item, color, size]
  );

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const pid = String(base?.mongoId || base?._id || base?.id || '').trim();
      const variantId = String(selectedVariantId || '').trim();
      if (!pid || !variantId) {
        setShippingState({ loading: false, options: [], fastestOption: null });
        return;
      }
      setShippingState((s) => ({ ...s, loading: true }));
      try {
        const data = await productService.getShippingOptions(pid, {
          countryCode: shippingCountry,
          quantity: qty,
          variantId,
        });
        if (cancelled) return;
        setShippingState({
          loading: false,
          options: Array.isArray(data?.options) ? data.options : [],
          fastestOption: data?.fastestOption || null,
        });
      } catch {
        if (cancelled) return;
        setShippingState({ loading: false, options: [], fastestOption: null });
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [base, selectedVariantId, shippingCountry, qty]);

  if (!isApiEnabled()) {
    return (
      <Container>
        <NotFound>
          <NotFoundTitle>{t('modamai.shop.liveCatalogDisabled')}</NotFoundTitle>
          <p style={{ opacity: 0.65, marginBottom: '1.5rem' }}>{t('modamai.shop.liveCatalogDisabledLead')}</p>
          <BackShop to="/">{t('modamai.product.backHome')}</BackShop>
        </NotFound>
      </Container>
    );
  }

  if (showApiLoading) {
    return (
      <Container>
        <NotFound>
          <NotFoundTitle>{t('modamai.collectionDetail.loadingTitle')}</NotFoundTitle>
          <p style={{ opacity: 0.65, marginBottom: '1.5rem' }}>{t('modamai.product.loadingLead')}</p>
        </NotFound>
      </Container>
    );
  }

  if (shouldTryApi && detailError) {
    return (
      <Container>
        <NotFound>
          <NotFoundTitle>{t('modamai.product.unavailableTitle')}</NotFoundTitle>
          <p style={{ opacity: 0.65, marginBottom: '1.5rem' }}>{String(detailError)}</p>
          <BackShop to="/shop/all">{t('modamai.product.backToShop')}</BackShop>
        </NotFound>
      </Container>
    );
  }

  if (!base || !item) {
    return (
      <Container>
        <NotFound>
          <NotFoundTitle>{t('modamai.product.notFoundTitle')}</NotFoundTitle>
          <p style={{ opacity: 0.65, marginBottom: '1.5rem' }}>{t('modamai.product.notFoundLead')}</p>
          <BackShop to="/shop/all">{t('modamai.product.backToShop')}</BackShop>
        </NotFound>
      </Container>
    );
  }

  const disableAdd =
    item.isAvailable === false ||
    (item.sizes?.length > 0 && !size) ||
    (item.colors?.length > 0 && !color);

  return (
    <div className="animate-fade-in">
      {apiDetail ? (
        <>
          <PageSeo
            documentTitle={item.name}
            description={item.metaDescription || item.description?.replace?.(/<[^>]+>/g, '').slice(0, 160)}
          />
          <ApiLocaleSeo
            canonicalUrl={apiDetail.canonicalUrl}
            hreflangAlternates={apiDetail.hreflangAlternates}
            hreflangLanguages={apiDetail.hreflangLanguages}
          />
        </>
      ) : null}
      <Container>
        <BreadcrumbNav aria-label={t('modamai.shop.breadcrumbAria')}>
          <CrumbLink to="/">{t('modamai.shop.home')}</CrumbLink>
          <ChevronRight size={12} aria-hidden />
          <CrumbLink to={`/shop/${encodeURIComponent(item.category)}`}>
            {item.category.replace(/^\w/, (m) => m.toUpperCase())}
          </CrumbLink>
          <ChevronRight size={12} aria-hidden />
          <CrumbCurrent>{item.name}</CrumbCurrent>
        </BreadcrumbNav>

        <Grid>
          <ImageCol>
            <ProductImageGallery
              product={item}
              productName={item.name}
              overlay={
                <ShareBtn type="button" aria-label={t('modamai.product.shareAria')} onClick={shareProduct}>
                  <Share2 size={16} aria-hidden />
                </ShareBtn>
              }
            />
          </ImageCol>

          <DetailCol>
            <BadgeRow>
              {showSaleBadge && (
                <BadgeSale>{t('modamai.product.offBadge', { percent: salePercent })}</BadgeSale>
              )}
              {item.tags.includes('new') && <BadgeNew>{t('modamai.product.badgeNew')}</BadgeNew>}
            </BadgeRow>
            <BrandLine>
              {item.brand || t('modamai.product.siteTitle')} · {item.collection}
            </BrandLine>
            <Title>{item.name}</Title>
            {reviewMeta && reviewMeta.count > 0 ? (
              <RatingHeroBtn type="button" onClick={scrollToReviews}>
                <StarRating rating={Number(reviewMeta.average) || 0} size="lg" />
                <RatingHeroText>
                  {(Number(reviewMeta.average) || 0).toFixed(1)} ·{' '}
                  {t('modamai.product.ratingReviews', { count: reviewMeta.count })}
                </RatingHeroText>
              </RatingHeroBtn>
            ) : null}

            <PriceRow>
              <Price>{formatPrice(item.price)}</Price>
              {hasCompare ? <WasPrice>{formatPrice(item.comparePrice)}</WasPrice> : null}
            </PriceRow>
            {item.saleEnds ? <SaleEnd>{item.saleEnds}</SaleEnd> : null}
            <TaxNote>{t('modamai.product.taxNote')}</TaxNote>

            {item.colors.length > 0 && (
              <ColorRow>
                <ColorHead>
                  <ColorLabel>
                    {t('modamai.product.colorLabel', { name: color || item.colors[0]?.name })}
                  </ColorLabel>
                </ColorHead>
                <ColorSwatches>
                  {item.colors.map((c, idx) => {
                    const name = c.name;
                    const active = color === name;
                    const hex = c.hex || '#ccc';
                    return (
                      <ColorSwatchBtn
                        key={`${name}-${idx}`}
                        type="button"
                        title={name}
                        $active={active}
                        $hex={hex}
                        aria-label={t('modamai.product.colorSwatchAria', { name })}
                        aria-pressed={active}
                        onClick={() => handleColorSelect(name)}
                      >
                        {active ? <Check size={18} style={{ color: lightSwatchFg(hex) }} /> : null}
                      </ColorSwatchBtn>
                    );
                  })}
                </ColorSwatches>
              </ColorRow>
            )}

            {item.sizes.length > 0 && (
              <SizeRow>
                <SizeHead>
                  <ColorLabel style={{ letterSpacing: '0.12em' }}>
                    {t('modamai.product.sizeLabel', { size: size || '—' })}
                  </ColorLabel>
                  <SizeGuideLink to="/size-guide">
                    <Ruler size={12} aria-hidden /> {t('modamai.product.sizeGuide')}
                  </SizeGuideLink>
                </SizeHead>
                <SizeChips>
                  {item.sizes.map((sz) => {
                    const avail = !color || isSizeAvailable(item, color, sz);
                    return (
                      <SizeBtn
                        key={sz}
                        type="button"
                        $active={size === sz}
                        $disabled={!avail}
                        disabled={!avail}
                        title={
                          !avail && color ? t('modamai.product.sizeUnavailableInColor', { color }) : undefined
                        }
                        onClick={() => avail && setSize(sz)}
                      >
                        {sz}
                      </SizeBtn>
                    );
                  })}
                </SizeChips>
              </SizeRow>
            )}

            <QtyBlock>
              <QtyLabel>{t('modamai.product.qtyLabel')}</QtyLabel>
              <QtyRow>
                <QtyAdj
                  type="button"
                  aria-label={t('modamai.product.decQtyAria')}
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                >
                  <Minus size={14} aria-hidden />
                </QtyAdj>
                <QtyVal>{qty}</QtyVal>
                <QtyAdj
                  type="button"
                  aria-label={t('modamai.product.incQtyAria')}
                  onClick={() => setQty((q) => q + 1)}
                >
                  <Plus size={14} aria-hidden />
                </QtyAdj>
              </QtyRow>
            </QtyBlock>

            <ActionRow>
              <AddBtn type="button" onClick={handleAdd} disabled={disableAdd}>
                <ShoppingBag size={16} aria-hidden />
                {t('modamai.product.addToBag')}
              </AddBtn>
              <WishLarge
                type="button"
                aria-label={
                  inWishlist ? t('modamai.product.wishlistRemoveAria') : t('modamai.product.wishlistAddAria')
                }
                aria-pressed={inWishlist}
                onClick={() => toggleWishlist(base.id)}
              >
                <Heart size={20} fill={inWishlist ? 'currentColor' : 'none'} aria-hidden />
              </WishLarge>
            </ActionRow>

            <ShippingPanel>
              <ShippingTop>
                <ShippingTitle>
                  <Truck size={14} /> Shipping options
                </ShippingTitle>
                <ShippingSelect value={shippingCountry} onChange={(e) => setShippingCountry(e.target.value)}>
                  {shippingCountries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </ShippingSelect>
              </ShippingTop>
              <ShippingPill>
                {shippingState.loading
                  ? 'Checking fastest delivery...'
                  : shippingState.fastestOption
                    ? `Fastest: ${shippingState.fastestOption.methodName || 'Shipping method'} · ${formatEtaDays(shippingState.fastestOption)}`
                    : 'Shipping options will appear after variant data is available.'}
              </ShippingPill>
              {shippingState.options.length > 0 && (
                <ShippingList>
                  {sortByQuickest(shippingState.options).slice(0, 5).map((opt, idx) => (
                    (shippingState.fastestOption?.methodName || '') === (opt.methodName || '') &&
                    formatEtaDays(shippingState.fastestOption) === formatEtaDays(opt) ? (
                      <FastestRow key={`${opt.methodName || 'method'}-${idx}`}>
                        <ShippingMethod>
                          {opt.methodName || 'Shipping Method'}
                          <FastestBadge>Fastest</FastestBadge>
                        </ShippingMethod>
                        <EtaText>{formatEtaDays(opt)}</EtaText>
                      </FastestRow>
                    ) : (
                      <ShippingRow key={`${opt.methodName || 'method'}-${idx}`}>
                        <ShippingMethod>{opt.methodName || 'Shipping Method'}</ShippingMethod>
                        <EtaText>{formatEtaDays(opt)}</EtaText>
                      </ShippingRow>
                    )
                  ))}
                </ShippingList>
              )}
            </ShippingPanel>

            <Highlights>
              {[
                { Icon: Truck, text: t('modamai.product.perkShipping') },
                { Icon: RotateCcw, text: t('modamai.product.perkReturns') },
                { Icon: ShoppingBag, text: t('modamai.product.perkService') },
              ].map(({ Icon, text }) => (
                <HiItem key={text}>
                  <Icon size={16} style={{ margin: '0 auto', opacity: 0.55 }} aria-hidden />
                  <HiText>{text}</HiText>
                </HiItem>
              ))}
            </Highlights>
          </DetailCol>
        </Grid>

        <ProductDetailTabs descriptionText={item.description} catalogProduct={item} apiProduct={null} />

        <ProductReviewsSection
          ref={reviewsRef}
          productSlugOrId={base.slug}
          productName={item.name}
          onMetaLoaded={onReviewsMetaLoaded}
        />

        {related.length > 0 && (
          <RelatedSection>
            <RelatedTitle>{t('modamai.product.relatedTitle')}</RelatedTitle>
            <RelatedGrid>
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Container>
    </div>
  );
}
